// Round only for display, using decimal digits rather than binary floats.
export function decimal(value: string | null | undefined, places = 2): string {
  if (typeof value !== 'string' || value.length > 4096 || !/^-?\d+(\.\d+)?$/.test(value))
    return '—';
  const negative = value.startsWith('-');
  const [whole, fraction = ''] = value.replace(/^-/, '').split('.');
  let scaled = BigInt(whole + fraction.padEnd(places, '0').slice(0, places));
  if (Number(fraction[places] || '0') >= 5) scaled += 1n;
  const digits = scaled.toString().padStart(places + 1, '0');
  const integer = places ? digits.slice(0, -places) : digits;
  return `${negative && scaled !== 0n ? '−' : ''}${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${places ? '.' + digits.slice(-places) : ''}`;
}
export function money(
  value: string | null | undefined,
  currency: string | null | undefined,
  signed = false,
  places = 2,
): string {
  const amount = decimal(value, places);
  if (amount === '—') return amount;
  const prefix =
    currency === 'USD' ? '$' : currency === 'CAD' ? 'CA$' : `${currency || 'Currency unknown'} `;
  const sign = amount.startsWith('−') ? '−' : signed && !/^0+(\.0+)?$/.test(amount) ? '+' : '';
  return `${sign}${prefix}${amount.replace(/^−/, '')}`;
}
export const isZero = (value: string | null | undefined) =>
  typeof value === 'string' && /^-?0+(\.0+)?$/.test(value);
/** CSS class for a signed decimal string: positive, negative, or muted when unknown/zero. */
export function tone(value: string | null | undefined): string {
  if (typeof value !== 'string' || !/^-?\d+(\.\d+)?$/.test(value) || isZero(value))
    return 'cost-basis-muted';
  return value.startsWith('-') ? 'cost-basis-negative' : 'cost-basis-positive';
}
/**
 * Exact decimal quotient of two decimal strings, rounded half away from zero to
 * `places`; null when either is unknown or the divisor is zero.
 */
export function divide(
  numerator: string | null | undefined,
  denominator: string | null | undefined,
  places = 6,
): string | null {
  const valid = (value: unknown): value is string =>
    typeof value === 'string' && value.length <= 4096 && /^-?\d+(\.\d+)?$/.test(value);
  if (!valid(numerator) || !valid(denominator)) return null;
  const scaled = (value: string) => {
    const [whole, fraction = ''] = value.replace(/^-/, '').split('.');
    const magnitude = BigInt(whole + fraction);
    return { value: value.startsWith('-') ? -magnitude : magnitude, scale: fraction.length };
  };
  const a = scaled(numerator);
  const b = scaled(denominator);
  if (b.value === 0n) return null;
  let quotient =
    (a.value * 10n ** BigInt(b.scale + places + 1)) / (b.value * 10n ** BigInt(a.scale));
  const negative = quotient < 0n;
  if (negative) quotient = -quotient;
  quotient = (quotient + 5n) / 10n;
  const digits = quotient.toString().padStart(places + 1, '0');
  const text = places ? `${digits.slice(0, -places)}.${digits.slice(-places)}` : digits;
  return negative && quotient !== 0n ? `-${text}` : text;
}
/**
 * A per-unit amount: cents, unless a nonzero amount would round to zero, which
 * keeps six decimals so it never reads as a reported zero.
 */
export function unitMoney(
  value: string | null | undefined,
  currency: string | null | undefined,
): string {
  // Decided on the unrounded value: 0.005 is below a cent though it rounds to 0.01.
  const belowCent = !isZero(value) && typeof value === 'string' && /^-?0\.00/.test(value);
  if (!belowCent) return money(value, currency);
  // Still nonzero below six decimals: say so rather than show a reported zero.
  if (/^-?0\.000000/.test(value as string))
    return `${(value as string).startsWith('-') ? '−' : ''}<${money('0.000001', currency, false, 6)}`;
  return money(value, currency, false, 6);
}
